import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactFlow, { Background, Controls, MiniMap, useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { Graph, Buildings, UsersThree, GlobeHemisphereWest, MapPin, UserCircle, Flower, User } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TYPE_STYLE = {
  member: { background: "#14281d", color: "#fff", border: "2px solid #d4a24e", fontWeight: 600, borderRadius: 999, padding: "12px 20px", fontSize: 14, width: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.25)" },
  country: { background: "#d4a24e", color: "#14281d", border: "none", fontWeight: 600, borderRadius: 12, padding: "8px 16px", fontSize: 13 },
  institution: { background: "#fff", color: "#14281d", border: "1.5px solid #14281d33", borderRadius: 12, padding: "10px 14px", fontSize: 12, width: 190 },
  group: { background: "#eef2ec", color: "#2c4636", border: "1.5px solid #2c463622", borderRadius: 12, padding: "10px 14px", fontSize: 12, width: 190 },
  person: { background: "#fff7ec", color: "#8a5a13", border: "1.5px solid #d4a24e", borderRadius: 999, padding: "7px 14px", fontSize: 11, fontWeight: 500, width: "auto", cursor: "pointer", boxShadow: "0 3px 10px rgba(212,162,78,.18)" },
  memorial_cluster: { background: "#4a3f5c", color: "#fff", border: "1.5px solid #b79bd6", borderRadius: 12, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  memorial: { background: "#f4f0f8", color: "#4a3f5c", border: "1.5px solid #cbb6e6", borderRadius: 14, padding: "8px 13px", fontSize: 11, width: 170, cursor: "pointer", boxShadow: "0 3px 10px rgba(120,90,160,.15)" },
};

function buildGraph(data, filter, showPeople) {
  const countryNodes = data.nodes.filter((n) => n.type === "country");
  const angleByCountry = {};
  countryNodes.forEach((c, i) => { angleByCountry[c.label] = -Math.PI / 2 + (2 * Math.PI * i) / (countryNodes.length || 1); });
  const childrenByCountry = {};
  data.nodes.forEach((n) => {
    if (n.type === "institution" || n.type === "group") {
      (childrenByCountry[n.country] = childrenByCountry[n.country] || []).push(n);
    }
  });
  const R_C = 300, R_CH = 620, SPREAD = 120;
  const pos = { member: { x: 0, y: 0 } };
  countryNodes.forEach((c) => {
    const a = angleByCountry[c.label];
    pos[c.id] = { x: Math.cos(a) * R_C, y: Math.sin(a) * R_C };
  });
  Object.entries(childrenByCountry).forEach(([country, kids]) => {
    const a = angleByCountry[country] ?? 0;
    kids.forEach((k, j) => {
      const off = (j - (kids.length - 1) / 2) * SPREAD;
      pos[k.id] = { x: Math.cos(a) * R_CH + Math.cos(a + Math.PI / 2) * off, y: Math.sin(a) * R_CH + Math.sin(a + Math.PI / 2) * off };
    });
  });

  // Classmates ("person" nodes) fan out just beyond the institution/group they share.
  const peopleByParent = {};
  data.edges.forEach((e) => {
    if (String(e.target).startsWith("person:")) {
      (peopleByParent[e.source] = peopleByParent[e.source] || []).push(e.target);
    }
  });
  Object.entries(peopleByParent).forEach(([parent, people]) => {
    const p = pos[parent] || { x: 0, y: 0 };
    const baseAng = Math.atan2(p.y, p.x);
    const baseDist = Math.hypot(p.x, p.y) || R_CH;
    const r = baseDist + 260;
    const span = Math.min(0.9, 0.22 * (people.length - 1));
    people.forEach((pid, k) => {
      const a = baseAng + (people.length > 1 ? -span / 2 + (span * k) / (people.length - 1) : 0);
      pos[pid] = { x: Math.cos(a) * r, y: Math.sin(a) * r };
    });
  });

  // In Memoriam cluster + memorial nodes (fanned below the member)
  const memCluster = data.nodes.find((n) => n.type === "memorial_cluster");
  if (memCluster) {
    pos[memCluster.id] = { x: 0, y: 460 };
    const mems = data.nodes.filter((n) => n.type === "memorial");
    mems.forEach((m, k) => { pos[m.id] = { x: (k - (mems.length - 1) / 2) * 200, y: 680 }; });
  }

  const dim = (n) => filter && filter !== "all" && n.type !== "member" && n.country !== filter && n.label !== filter;
  const visible = data.nodes.filter((n) => showPeople || n.type !== "person");
  const nodes = visible.map((n) => {
    const memorial = n.type === "person" && n.in_memoriam;
    const label = memorial
      ? `In memory of\n${n.label}${n.meta?.passed_year ? `\nD. ${n.meta.passed_year}` : ""}`
      : (n.type === "institution" && n.meta?.end_year ? `${n.label}\n(${n.meta.start_year || ""}–${n.meta.end_year})` : n.label);
    const base = memorial
      ? { ...TYPE_STYLE.person, background: "#ece0c8", color: "#5a4527", border: "1.5px solid #a9863f", boxShadow: "0 3px 12px rgba(120,90,40,.22)", fontStyle: "italic" }
      : TYPE_STYLE[n.type];
    return {
      id: n.id,
      position: pos[n.id] || { x: 0, y: 0 },
      data: { label, raw: n },
      style: { ...base, textAlign: "center", whiteSpace: "pre-line", opacity: dim(n) ? 0.12 : 1, transition: "opacity .3s" },
      draggable: true,
    };
  });
  const edges = data.edges
    .filter((e) => showPeople || !String(e.target).startsWith("person:"))
    .map((e, i) => {
      const src = data.nodes.find((n) => n.id === e.source);
      const tgt = data.nodes.find((n) => n.id === e.target);
      const isPerson = String(e.target).startsWith("person:");
      const faded = filter && filter !== "all" && ((src && dim(src)) || (tgt && dim(tgt)));
      return { id: `e${i}`, source: e.source, target: e.target, type: "smoothstep", animated: e.source === "member",
               style: { stroke: isPerson ? "#d4a24e" : "#14281d", strokeWidth: 1.5, strokeDasharray: isPerson ? "4 4" : undefined, opacity: faded ? 0.06 : isPerson ? 0.5 : 0.4 } };
    });
  return { nodes, edges };
}

export default function AffiliationMap() {
  useSeo("My Alumni Network", "Your personal map of institutions, alumni groups and classmates across the world.");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showPeople, setShowPeople] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selected, setSelected] = useState(null);
  const [year, setYear] = useState("");

  const loadMap = useCallback(() => { api.get("/network/affiliation-map").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  useEffect(() => { loadMap(); }, [loadMap]);
  useEffect(() => {
    if (!data || data === false) return;
    const g = buildGraph(data, filter, showPeople);
    setNodes(g.nodes); setEdges(g.edges);
  }, [data, filter, showPeople, setNodes, setEdges]);

  const connections = data && data.stats ? (data.stats.connections || 0) : 0;

  const onNodeClick = useCallback((_e, node) => {
    const raw = node.data?.raw;
    if (!raw) return;
    if (raw.type === "group" && raw.meta?.group_id) navigate(`/groups/${raw.meta.group_id}`);
    else if (raw.type === "institution") navigate(raw.meta?.slug ? `/site/${raw.meta.slug}` : "/institutions");
    else if (raw.type === "person" && raw.meta?.user_id) { setYear(raw.meta?.passed_year || ""); setSelected(raw); }
    else if (raw.type === "country") setFilter((f) => (f === raw.label ? "all" : raw.label));
    else if (raw.type === "memorial" && raw.meta?.memorial_id) navigate(`/memorials/${raw.meta.memorial_id}`);
    else if (raw.type === "memorial_cluster") navigate("/memorials");
  }, [navigate]);

  const saveMemorial = async () => {
    try {
      await api.post("/network/memorials", { person_user_id: selected.meta.user_id, name: selected.label, year });
      toast.success(`${selected.label} added to your In Memoriam`);
      setSelected(null); loadMap();
    } catch { toast.error("Could not save"); }
  };
  const removeMemorial = async () => {
    try {
      await api.delete(`/network/memorials/${selected.meta.user_id}`);
      toast.success("Memorial removed");
      setSelected(null); loadMap();
    } catch { toast.error("Could not remove"); }
  };

  if (data === false) return <main className="p-16 text-center text-muted-foreground">Could not load your network.</main>;

  return (
    <main className="flex h-[calc(100vh-4rem)] flex-col" data-testid="affiliation-map-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-5 py-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-light"><Graph size={24} weight="duotone" className="text-secondary" /> My alumni network</h1>
          {data && <p className="mt-0.5 text-sm text-muted-foreground">{data.stats.institutions} institution{data.stats.institutions !== 1 ? "s" : ""} · {data.stats.groups} group{data.stats.groups !== 1 ? "s" : ""} · {data.stats.countries} countr{data.stats.countries !== 1 ? "ies" : "y"}{connections > 0 ? ` · ${connections} classmate${connections !== 1 ? "s" : ""}` : ""}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="hidden items-center gap-3 text-xs text-muted-foreground sm:flex">
            <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#d4a24e" }} /> Country</span>
            <span className="flex items-center gap-1"><Buildings size={13} /> Institution</span>
            <span className="flex items-center gap-1"><UsersThree size={13} /> Group</span>
            <span className="flex items-center gap-1"><UserCircle size={13} weight="fill" className="text-[#d4a24e]" /> Classmate</span>
          </div>
          {connections > 0 && (
            <label className="flex items-center gap-2 rounded-full border border-border/60 px-3 py-1.5 text-xs font-medium" data-testid="toggle-classmates">
              <Switch checked={showPeople} onCheckedChange={setShowPeople} data-testid="toggle-classmates-switch" />
              Show classmates
            </label>
          )}
          {data && data.countries.length > 1 && (
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44 rounded-full" data-testid="map-country-filter"><GlobeHemisphereWest size={15} className="mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All geographies</SelectItem>{data.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="relative flex-1">
        {data && data.nodes.filter((n) => n.type !== "person").length <= 1 ? (
          <div className="grid h-full place-items-center text-center">
            <div>
              <MapPin size={40} weight="duotone" className="mx-auto text-muted-foreground" />
              <p className="mt-3 text-muted-foreground">Add your education and join a few groups<br />to grow your alumni network map.</p>
            </div>
          </div>
        ) : (
          <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick} fitView fitViewOptions={{ padding: 0.25 }} minZoom={0.15} maxZoom={1.8}
            proOptions={{ hideAttribution: true }} nodesConnectable={false}>
            <Background color="#14281d10" gap={22} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable nodeColor={(n) => (n.data?.raw?.type === "member" ? "#14281d" : n.data?.raw?.type === "country" ? "#d4a24e" : n.data?.raw?.type === "person" ? "#e8c583" : (n.data?.raw?.type === "memorial" || n.data?.raw?.type === "memorial_cluster") ? "#b79bd6" : "#9bb0a3")} />
          </ReactFlow>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent data-testid="map-person-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-medium">{selected?.label}</DialogTitle>
            <DialogDescription>{selected?.in_memoriam ? "This classmate is remembered on your map." : "A classmate in your network."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button variant="outline" className="w-full rounded-full gap-1.5" onClick={() => { const s = selected; setSelected(null); navigate(`/u/${s.meta.user_id}`); }} data-testid="map-view-profile">
              <User size={16} /> View profile
            </Button>
            <div className="rounded-xl border border-[#a9863f]/40 bg-[#ece0c8]/50 p-4">
              <p className="flex items-center gap-2 font-heading text-base font-medium text-[#5a4527]"><Flower size={18} weight="duotone" /> In memoriam</p>
              {selected?.in_memoriam ? (
                <div className="mt-2 space-y-3">
                  <p className="text-sm text-[#6b5836]">Marked as passed{selected?.meta?.passed_year ? ` · D. ${selected.meta.passed_year}` : ""}.</p>
                  <Button variant="outline" className="rounded-full gap-1.5 text-destructive hover:text-destructive" onClick={removeMemorial} data-testid="map-remove-memorial"><Flower size={15} /> Remove memorial</Button>
                </div>
              ) : (
                <div className="mt-2 space-y-3">
                  <p className="text-sm text-[#6b5836]">If this classmate has passed away, add a memorial badge to honour them on your map.</p>
                  <div className="space-y-1.5">
                    <Label className="text-[#5a4527]">Year of passing (optional)</Label>
                    <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2019" data-testid="map-memorial-year" />
                  </div>
                  <Button className="rounded-full gap-1.5 bg-[#8a6d3b] text-white hover:bg-[#6b5836]" onClick={saveMemorial} data-testid="map-mark-memorial"><Flower size={15} weight="fill" /> Mark in memoriam</Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
